import {
  isDefaultSheetsHost,
  isLikelyWorkspaceCustomHost
} from './sheetDomains';

const joinUrl = (origin, path) => {
  const safeOrigin = `${origin || ''}`.replace(/\/+$/, '');
  const safePath = `${path || ''}`.startsWith('/') ? `${path || ''}` : `/${path || ''}`;
  return `${safeOrigin}${safePath}`;
};

export const PARROT_DOCS_BRAND = {
  appName: 'ParrotDocs',
  shortName: 'ParrotDocs',
  title: 'PARROT DOCS',
  description: 'PARROT DOCS - Modern document management and document tools',
  ogTitle: 'PARROT DOCS - Modern document management and document tools',
  ogDescription: 'Save your docs, summarise, analyse and repurpose with AI',
  ogImage: 'https://storage.googleapis.com/parrotai/Parrot%20Docs/ParrotDocs%20ogImage.png',
  favicon: '/favicon.png',
  themeColor: '#000000'
};

export const PARROT_SHEETS_BRAND = {
  appName: 'ParrotSheets',
  shortName: 'ParrotSheets',
  title: 'ParrotSheets | Collaborative Sheets And Workspaces',
  description: 'Build collaborative sheets, client workspaces, templates, and shared workflows in ParrotSheets.',
  ogTitle: 'ParrotSheets | Collaborative Sheets And Workspaces',
  ogDescription: 'Launch structured sheets, shared workspaces, templates, and row-level collaboration from one focused workspace.',
  ogImagePath: '/parrotsheets-og.svg',
  favicon: '/parrotsheets-favicon.svg',
  themeColor: '#0b6b6b',
  pricingPlans: [
    {
      id: 'free',
      name: 'Free',
      price: 'R0',
      cadence: '/month',
      summary: 'For solo operators testing the workflow.',
      cta: 'Start Free',
      featured: false,
      features: [
        'Up to 3 active sheets',
        '1 workspace',
        'Public and restricted sharing',
        'Template cloning'
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 'R180',
      yearlyPrice: 'R1800',
      monthlyInternationalPrice: '$10/month outside South Africa',
      yearlyInternationalPrice: '$100/year outside South Africa',
      summary: 'For teams running live sheets with internal staff and controlled workspace capacity.',
      cta: 'Choose Pro',
      featured: true,
      features: [
        'Up to 20 sheets across up to 2 workspaces',
        'Up to 50 internal users',
        'Internal email sending included',
        'Internal and external SMS via credits at R0.40 each',
        'Minimum SMS top-up is 500 credits'
      ]
    },
    {
      id: 'business',
      name: 'Business',
      monthlyPrice: 'R540',
      yearlyPrice: 'R5400',
      monthlyInternationalPrice: '$25/month outside South Africa',
      yearlyInternationalPrice: '$250/year outside South Africa',
      summary: 'For larger teams that need more sheets, more workspaces, and a predictable monthly email allowance.',
      cta: 'Contact Sales',
      featured: false,
      features: [
        'Up to 100 sheets',
        'Up to 10 workspaces',
        '100 emails per month',
        'Internal and external SMS via credits at R0.40 each',
        'Minimum SMS top-up is 500 credits'
      ]
    },
    {
      id: 'agency',
      name: 'Agency',
      monthlyPrice: 'R120',
      yearlyPrice: 'R1200',
      monthlyInternationalPrice: null,
      yearlyInternationalPrice: '$70/workspace/year outside South Africa',
      summary: 'For agencies creating and managing separate workspaces for client organisations.',
      cta: 'Talk To Us',
      featured: false,
      features: [
        'Unlimited sheets',
        'Unlimited workspaces',
        '100 emails per workspace per month',
        'Internal and external SMS via credits at R0.40 each',
        'Minimum SMS top-up is 500 credits'
      ]
    }
  ]
};

export const isSheetsSurface = ({
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : ''
} = {}) => (
  isDefaultSheetsHost(hostname)
  || isLikelyWorkspaceCustomHost(hostname)
  || `${pathname || ''}`.startsWith('/sheets')
  || `${pathname || ''}`.startsWith('/sheet/')
  || `${pathname || ''}`.startsWith('/workspace/')
);

export const getActiveBrand = (options = {}) => (
  isSheetsSurface(options) ? PARROT_SHEETS_BRAND : PARROT_DOCS_BRAND
);

const upsertMetaTag = (selector, attributeName, attributeValue, content) => {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertLinkTag = (selector, rel, href) => {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

export const applyDocumentBranding = (brand, options = {}) => {
  if (typeof document === 'undefined' || !brand) return;

  const origin = options.origin || window.location.origin;
  const pageUrl = options.url || window.location.href;
  const title = options.title || brand.title;
  const description = options.description || brand.description;
  const ogTitle = options.ogTitle || brand.ogTitle || title;
  const ogDescription = options.ogDescription || brand.ogDescription || description;
  const ogImage = options.ogImage || (
    brand.ogImagePath ? joinUrl(origin, brand.ogImagePath) : brand.ogImage
  );

  document.title = title;

  upsertMetaTag('meta[name="description"]', 'name', 'description', description);
  upsertMetaTag('meta[name="theme-color"]', 'name', 'theme-color', brand.themeColor || '#000000');
  upsertMetaTag('meta[property="og:title"]', 'property', 'og:title', ogTitle);
  upsertMetaTag('meta[property="og:description"]', 'property', 'og:description', ogDescription);
  upsertMetaTag('meta[property="og:image"]', 'property', 'og:image', ogImage);
  upsertMetaTag('meta[property="og:url"]', 'property', 'og:url', pageUrl);
  upsertMetaTag('meta[property="og:type"]', 'property', 'og:type', 'website');
  upsertMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
  upsertMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', ogTitle);
  upsertMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', ogDescription);
  upsertMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

  if (brand.favicon) {
    upsertLinkTag('link[rel="icon"]', 'icon', brand.favicon);
    upsertLinkTag('link[rel="apple-touch-icon"]', 'apple-touch-icon', brand.favicon);
  }
};
